import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsOfService() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-surface-2">
      {/* Header */}
      <div className="bg-surface shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-accent hover:underline mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back', 'Volver')}
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-accent" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-ink">
              {t('legal.termsOfService.title', 'Términos de Servicio')}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-surface rounded-xl shadow-sm p-6 sm:p-8 space-y-6">
          <p className="text-sm text-ink-muted">
            {t('legal.lastUpdated', 'Última actualización')}: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.acceptance.title', 'Aceptación de los términos')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.termsOfService.acceptance.content', 'Al acceder y utilizar Monkey Barber, aceptas cumplir con estos términos de servicio. Si no estás de acuerdo con alguna parte de los términos, no podrás acceder al servicio.')}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.description.title', 'Descripción del servicio')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.termsOfService.description.content', 'Monkey Barber es una plataforma de gestión de turnos y citas para barberías. Permite a los usuarios reservar turnos, gestionar citas y acumular puntos de fidelización.')}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.account.title', 'Cuentas de usuario')}
            </h2>
            <ul className="list-disc list-inside text-ink-muted space-y-2">
              <li>{t('legal.termsOfService.account.responsibility', 'Eres responsable de mantener la confidencialidad de tu cuenta')}</li>
              <li>{t('legal.termsOfService.account.accuracy', 'Debes proporcionar información precisa y actualizada')}</li>
              <li>{t('legal.termsOfService.account.activity', 'Eres responsable de toda actividad que ocurra bajo tu cuenta')}</li>
              <li>{t('legal.termsOfService.account.notification', 'Debes notificarnos inmediatamente sobre cualquier uso no autorizado')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.conduct.title', 'Conducta del usuario')}
            </h2>
            <p className="text-ink-muted mb-3">
              {t('legal.termsOfService.conduct.intro', 'Al usar nuestro servicio, aceptas NO:')}
            </p>
            <ul className="list-disc list-inside text-ink-muted space-y-2">
              <li>{t('legal.termsOfService.conduct.abuse', 'Abusar o usar el servicio para fines ilegales')}</li>
              <li>{t('legal.termsOfService.conduct.spam', 'Enviar spam o contenido no solicitado')}</li>
              <li>{t('legal.termsOfService.conduct.impersonate', 'Hacerte pasar por otra persona o entidad')}</li>
              <li>{t('legal.termsOfService.conduct.interfere', 'Interferir con el funcionamiento normal del servicio')}</li>
              <li>{t('legal.termsOfService.conduct.noShow', 'Reservar turnos sin intención de acudir repetidamente')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.appointments.title', 'Política de citas y turnos')}
            </h2>
            <ul className="list-disc list-inside text-ink-muted space-y-2">
              <li>{t('legal.termsOfService.appointments.arrival', 'Debes llegar a tiempo para tu turno o cita')}</li>
              <li>{t('legal.termsOfService.appointments.cancellation', 'Si no puedes asistir, cancela tu turno con antelación')}</li>
              <li>{t('legal.termsOfService.appointments.noShow', 'Las ausencias repetidas pueden resultar en restricciones de la cuenta')}</li>
              <li>{t('legal.termsOfService.appointments.courtesy', 'El tiempo de cortesía es definido por cada establecimiento')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.loyalty.title', 'Programa de fidelización')}
            </h2>
            <ul className="list-disc list-inside text-ink-muted space-y-2">
              <li>{t('legal.termsOfService.loyalty.points', 'Los puntos se acumulan según las políticas de cada establecimiento')}</li>
              <li>{t('legal.termsOfService.loyalty.expiration', 'Los puntos y recompensas pueden tener fecha de caducidad')}</li>
              <li>{t('legal.termsOfService.loyalty.nonTransferable', 'Los puntos y recompensas no son transferibles')}</li>
              <li>{t('legal.termsOfService.loyalty.changes', 'Nos reservamos el derecho de modificar el programa')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.liability.title', 'Limitación de responsabilidad')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.termsOfService.liability.content', 'Monkey Barber actúa como intermediario entre clientes y establecimientos. No somos responsables de la calidad de los servicios prestados por los barberos o establecimientos asociados.')}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.modifications.title', 'Modificaciones del servicio')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.termsOfService.modifications.content', 'Nos reservamos el derecho de modificar o discontinuar el servicio en cualquier momento. Notificaremos cambios significativos con antelación razonable.')}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.termination.title', 'Terminación')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.termsOfService.termination.content', 'Podemos suspender o terminar tu acceso al servicio si violas estos términos. Puedes cancelar tu cuenta en cualquier momento desde tu perfil.')}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.law.title', 'Ley aplicable')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.termsOfService.law.content', 'Estos términos se rigen por las leyes de España. Cualquier disputa será resuelta en los tribunales competentes.')}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink mb-3">
              {t('legal.termsOfService.contact.title', 'Contacto')}
            </h2>
            <p className="text-ink-muted">
              {t('legal.termsOfService.contact.content', 'Si tienes preguntas sobre estos términos de servicio, puedes contactarnos a través de nuestra página de contacto.')}
            </p>
          </section>

          {/* Links to other policies */}
          <div className="border-t border-hairline pt-6 mt-8">
            <h3 className="text-sm font-medium text-ink mb-3">
              {t('legal.relatedPolicies', 'Políticas relacionadas')}
            </h3>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/cookies-policy"
                className="text-accent hover:underline text-sm"
              >
                {t('cookies.cookiesPolicy', 'Política de Cookies')}
              </Link>
              <Link
                to="/privacy-policy"
                className="text-accent hover:underline text-sm"
              >
                {t('cookies.privacyPolicy', 'Política de Privacidad')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
